'use client'

import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog'
import * as React from 'react'

import { cn } from '../../lib'

import { buttonV1Style } from '../button/button-v1.css'

/**
 * @deprecated Use AlertDialog instead
 */
const AlertDialogV1 = AlertDialogPrimitive.Root

const AlertDialogV1Trigger = AlertDialogPrimitive.Trigger

const AlertDialogV1Portal = AlertDialogPrimitive.Portal

const AlertDialogV1Overlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      'fixed inset-0 z-[60] bg-[#1c1c1c]/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
    ref={ref}
  />
))

const AlertDialogV1Content = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> & {
    isInternalDialog?: boolean
    onOverlayClick?: () => void
  }
>(({ className, isInternalDialog = true, onOverlayClick, ...props }, ref) => (
  <AlertDialogV1Portal>
    {isInternalDialog && <AlertDialogV1Overlay onClick={onOverlayClick} />}
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        'fixed z-[61] left-[calc(50%_-_calc(var(--removed-body-scroll-bar-size)/2))] top-[50%] translate-x-[-50%] translate-y-[-50%]',
        ' bg-white border rounded-lg',
        'shadow-lg',
        'flex flex-col gap-8 p-5 gap-5',
        'w-[calc(100vw-40px)] mobile:min-w-[335px] tablet:max-w-[420px]',
        'duration-200',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        className,
      )}
      {...props}
    />
  </AlertDialogV1Portal>
))

const AlertDialogV1Header = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-[5px]', className)} {...props} />
)

const AlertDialogV1Image = ({ src, alt = 'Alert dialog image' }: { src: string; alt?: string }) => (
  <div className="flex justify-center items-center mb-[15px]">
    <div className="relative w-full max-w-60 h-[180px]">
      <img src={src} alt={alt} className="object-contain w-full h-full" />
    </div>
  </div>
)

const AlertDialogV1Footer = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col-reverse', className)} {...props} />
)

const AlertDialogV1Title = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn('flex text-lg text-[16px] font-medium items-center justify-center', className)}
    {...props}
  />
))

const AlertDialogV1Description = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
))

const AlertDialogV1Action = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action ref={ref} className={cn(buttonV1Style({ size: 'xLarge' }), className)} {...props} />
))

const AlertDialogV1Cancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ ...props }, ref) => <AlertDialogPrimitive.Cancel ref={ref} {...props} />)

AlertDialogV1Overlay.displayName = AlertDialogPrimitive.Overlay.displayName
AlertDialogV1Content.displayName = AlertDialogPrimitive.Content.displayName
AlertDialogV1Header.displayName = 'AlertDialogV1Header'
AlertDialogV1Footer.displayName = 'AlertDialogV1Footer'
AlertDialogV1Title.displayName = AlertDialogPrimitive.Title.displayName
AlertDialogV1Description.displayName = AlertDialogPrimitive.Description.displayName
AlertDialogV1Action.displayName = AlertDialogPrimitive.Action.displayName
AlertDialogV1Cancel.displayName = AlertDialogPrimitive.Cancel.displayName

export {
  AlertDialogV1,
  AlertDialogV1Action,
  AlertDialogV1Cancel,
  AlertDialogV1Content,
  AlertDialogV1Description,
  AlertDialogV1Footer,
  AlertDialogV1Header,
  AlertDialogV1Image,
  AlertDialogV1Overlay,
  AlertDialogV1Portal,
  AlertDialogV1Title,
  AlertDialogV1Trigger,
}
